import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations';
import api from '../utils/api';

const ForgotPassword = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await api.post('/auth/forgot-password', { email });
      setSuccess(true);
    } catch (error) {
      setError(error.response?.data?.error || (language === 'en'
        ? 'Failed to send reset email. Please try again.'
        : 'فشل إرسال بريد إعادة التعيين. حاول مرة أخرى.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-blackC flex items-center justify-center px-6 py-12">
      <div className={`w-full max-w-md transition-all duration-1000 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        {/* Logo */}
        <div className="text-center mb-10">
          <Link to="/" className="inline-block">
            <span className="font-display text-4xl">
              <span className="text-primary">L</span>
              <span className="text-cream">imousine</span>
            </span>
          </Link>
        </div>

        {/* Header */}
        <div className="mb-10 text-center">
          <h1 className="font-display text-4xl text-cream mb-4">
            {language === 'en' ? 'Forgot Password?' : 'نسيت كلمة المرور؟'}
          </h1>
          <p className="text-gray-light">
            {language === 'en'
              ? 'Enter your email address and we\'ll send you a link to reset your password.'
              : 'أدخل عنوان بريدك الإلكتروني وسنرسل لك رابطاً لإعادة تعيين كلمة المرور.'}
          </p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-8 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-green-400 font-medium">
                  {language === 'en' ? 'Email Sent!' : 'تم الإرسال!'}
                </p>
                <p className="text-green-400/70 text-sm mt-1">
                  {language === 'en'
                    ? 'If an account with that email exists, a password reset link has been sent. Please check your inbox.'
                    : 'إذا كان هناك حساب بهذا البريد الإلكتروني، تم إرسال رابط إعادة تعيين كلمة المرور. يرجى التحقق من بريدك الوارد.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-8 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-red-400 font-medium">
                  {language === 'en' ? 'Error' : 'خطأ'}
                </p>
                <p className="text-red-400/70 text-sm mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        {!success && (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div className="relative group">
              <label
                className={`absolute transition-all duration-300 pointer-events-none ${focusedField === 'email' || email
                  ? 'text-xs text-primary top-2 left-4'
                  : 'text-gray-light top-1/2 -translate-y-1/2 left-14'
                  }`}
              >
                {t.email}
              </label>
              <div className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors duration-300 ${focusedField === 'email' ? 'text-primary' : 'text-[#4a4a4a]'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
                onFocus={() => setFocusedField('email')}
                onBlur={() => setFocusedField(null)}
                className="w-full bg-dark border border-gray-dark text-cream pl-14 pr-4 pt-6 pb-3 focus:outline-none focus:border-primary/50 focus:bg-charcoal transition-all duration-300 rounded-lg"
                required
                dir="ltr"
              />
              <div className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-primary to-primary-light transition-all duration-500 rounded-full ${focusedField === 'email' ? 'w-full' : 'w-0'}`} />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="relative w-full group overflow-hidden"
            >
              <div className="relative bg-gradient-to-r from-primary to-primary-light text-blackC font-semibold py-4 px-6 flex items-center justify-center gap-3 rounded-lg transition-all duration-300 group-hover:shadow-lg group-hover:shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-blackC/20 border-t-blackC rounded-full animate-spin" />
                    {language === 'en' ? 'Sending...' : 'جاري الإرسال...'}
                  </>
                ) : (
                  <>
                    {language === 'en' ? 'Send Reset Link' : 'إرسال رابط إعادة التعيين'}
                    <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </>
                )}
              </div>
              <div className="absolute inset-0 bg-white/20 translate-x-[-100%] skew-x-12 group-hover:translate-x-[100%] transition-transform duration-700" />
            </button>
          </form>
        )}

        {/* Back to Login */}
        <div className="mt-8 text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-gray-light hover:text-primary transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {language === 'en' ? 'Back to Login' : 'العودة لتسجيل الدخول'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;

